//@ts-nocheck
import { EditIcon, MinusIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Formik } from "formik";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { boolean, object, string } from "yup";
import { FormsetWrapper } from "../components/FormWrapper";
import { OverlayRouter } from "../components/OverlayRouter";
import { FFormContext } from "../primitives/forms/FormField";
import { FFormField } from "../primitives/landing-pages";
import { IEducationWorkHistoryStore } from "../stores";
import { scrollToTop, toTitleCase } from "../utils/functions";
import workHistoryData from "./formData/workHistory.json";
import { Formset } from "./forms/FormSet";

function startYearOptions(minYears = 40) {
  let currentYear = new Date().getFullYear();
  let startYear = currentYear - minYears;
  let options: Array<any> = [];
  for (let i = startYear; i < currentYear + 1; i++) {
    options = [...options, i];
  }
  return options.map((item) => ({ label: item, value: item.toString() }));
}

function endYearOptions(startYear?: any, minYears = 40) {
  let currentYear = new Date().getFullYear();
  let beginYear = startYear ? startYear : currentYear - minYears;
  let endYear = currentYear + 10;
  let options: Array<any> = [];
  for (let i = beginYear; i < endYear; i++) {
    options = [...options, i];
  }
  return options.map((item) => ({ label: item, value: item.toString() }));
}

const workHistoryInitialValue = {
  company: "",
  role: "",
  isTeachingRole: false,
  startYear: "",
  endYear: "",
  isCurrent: false,
  showOnProfile: true,
  classGroup: [],
};

const WorkHistoryFormRaw = (props: any) => {
  const { value, onSubmit, showFormError, innerRef } = props;
  const [companyName, setCompanyName] = useState(value ? value.company : "");
  const { placeholders, validationData, present } = workHistoryData;

  const workValidationSchema = object().shape({
    company: string().required(validationData.company.required),
    role: string().required(validationData.role.required),
    isTeachingRole: boolean().required(),
    isCurrent: boolean().required(),
    showOnProfile: boolean().required(),
    startYear: string().required(validationData.startYear.required),
    endYear: string().when("isCurrent", {
      is: false,
      then: string().required(validationData.endYear.required),
      otherwise: string(),
    }),
  });

  let commonProps = (
    { name }: { name: any },
    { touched, errors }: { touched: any; errors: any }
  ) => {
    return {
      ...placeholders[name],
      isInvalid: Boolean(touched[name] && errors[name]),
      errorMessage: errors[name],
    };
  };

  return (
    <Formik
      validationSchema={workValidationSchema}
      innerRef={innerRef}
      initialValues={{ ...workHistoryInitialValue, ...value }}
      onSubmit={(values) => {
        let newValues = {
          ...values,
          company: toTitleCase(values.company),
          role: toTitleCase(values.role),
        };
        onSubmit(newValues);
      }}
    >
      {({ values, handleSubmit, handleBlur }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Stack spacing={6}>
              {showFormError && (
                <Box pt={2}>
                  <Alert rounded="lg" status="error">
                    <AlertIcon />
                    {workHistoryData.formTitle.errorMessage}
                  </Alert>
                </Box>
              )}
              <FFormField
                name="company"
                onBlur={(e: any) => {
                  handleBlur(e);
                  let value = toTitleCase(e.target.value);
                  setCompanyName(value);
                }}
              />
              <FFormField
                name="role"
                label={
                  companyName
                    ? `What's your role at ${companyName}?`
                    : "What's your role?"
                }
              />
              <FFormField name="isTeachingRole" type="checkbox" />

              <Flex justify="space-between" align="center">
                <FFormField
                  name="startYear"
                  type="select"
                  key={`${startYearOptions().length > 0}`}
                  options={startYearOptions()}
                  onChange={(v: any, form: any) => {
                    if (values.endYear && values.endYear < v) {
                      form.setFieldValue("endYear", v);
                    }
                  }}
                />

                <Box w={6} h={6} />

                {!values.isCurrent ? (
                  <FFormField
                    name="endYear"
                    type="select"
                    options={
                      values.startYear
                        ? endYearOptions(values.startYear)
                        : endYearOptions()
                    }
                  />
                ) : (
                  <Stack
                    flex={1}
                    pt={6}
                    isInline
                    align="center"
                    color="neutral.800"
                  >
                    <MinusIcon />
                    <Text fontWeight="medium" ml={2}>
                      {present}
                    </Text>
                  </Stack>
                )}
              </Flex>

              <FFormField name="isCurrent" type="checkbox" />
              <Box
                p={[4, 5]}
                borderColor="blue.500"
                borderStyle="dashed"
                borderWidth="xs"
                rounded="md"
              >
                <Stack isInline align="center" spacing={[4, 5]}>
                  <FFormField name="showOnProfile" type="switch" />
                  <Box>
                    <Text fontWeight="medium">
                      {placeholders.showOnProfile.heading}
                    </Text>
                    <Text fontSize="sm">
                      {placeholders.showOnProfile.description}
                    </Text>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </form>
        );
      }}
    </Formik>
  );
};

export const WorkHistoryForm = observer(WorkHistoryFormRaw);

export const WorkHistory = ({
  field: workHistory,
  onEdit,
  ...rest
}: {
  field: any;
  onEdit: any;
}) => {
  const { company, isCurrent, role, startYear, endYear } = workHistory;
  const showEndYear = startYear !== endYear;

  return (
    <Box>
      {company && (
        <Flex justify="space-between" align="baseline">
          <Box flex={1}>
            <Text fontWeight="semibold">{role}</Text>
            <Text>{company}</Text>

            {!startYear && (
              <Link mt={2} onClick={onEdit} color="red.500" fontSize="xs">
                Some fields missing. Click to update
              </Link>
            )}
          </Box>

          <Stack ml={[3, 6]} align="center">
            <Button
              size="sm"
              leftIcon={<EditIcon />}
              variant="link"
              colorScheme="blue"
              onClick={onEdit}
            >
              {startYear ? "Edit" : "Update"}
            </Button>
            {!startYear && (
              <Text
                fontSize="xs"
                py="1px"
                px={2}
                rounded="full"
                borderWidth="3xs"
                borderColor="red.500"
                color="red.500"
              >
                some errors
              </Text>
            )}
          </Stack>
        </Flex>
      )}
    </Box>
  );
};

const WorkHistoryFormPage = observer(
  ({
    store,
    loading,
    textData,
    label,
    onSubmit,
  }: {
    store: IEducationWorkHistoryStore;
    spacing?: number;
    loading?: boolean;
    label: string;
    textData: any;
    onSubmit?: (values: any, label?: any) => Promise<any>;
  }) => {
    React.useEffect(() => scrollToTop(), []);
    const [showFormError, setShowFormError] = React.useState(false);

    return (
      <Formset
        isLoading={loading}
        formsetValues={store.workHistories}
        Component={WorkHistory}
        onEdit={store.editWorkHistory}
        onAdd={store.addWorkHistory}
        addButtonText="Add new work history"
        onSave={async (data: any) => {
          if (data.company && data.role) {
            store.saveWorkHistory(data);
            if (onSubmit) {
              return onSubmit(data, label);
            }
          }
        }}
        validateForm={(values) => {
          let { company, role, startYear } = values;
          let isValid = Boolean(company && role && startYear);
          if (isValid) {
            setShowFormError(false);
            return isValid;
          } else {
            setShowFormError(true);
          }
        }}
        ComponentForm={WorkHistoryForm}
        formHeaderText="Work history"
        additionalFormProps={{ showFormError }}
        currentFormIndex={store.currentWorkHistoryIndex}
        formDefaultValue={{}}
        onRemove={store.removeWorkHistory}
        saveButtontext={textData.buttonText.save}
      />
    );
  }
);
export type WorkhistoryPageType = {
  store: IEducationWorkHistoryStore;
  formsetDescription: string;
  loading: boolean;
  formHeader: string;
  displayType: string;
  label: string;
  lockedDescription: string;
  shouldDisplayButton: boolean;
  buttonText: string;
  textData?: any;
  completed: boolean;
  spacing?: number;
  onSubmit: any;
  updateStore?: boolean;
  onBack?: () => void;
  inContainer?: boolean;
};
const WorkHistoryPage: React.FC<WorkhistoryPageType> = observer(
  ({
    store,
    onSubmit,
    loading,
    label,
    textData = workHistoryData,
    onBack,
    updateStore,
    inContainer,
  }) => {
    const render = (
      <OverlayRouter>
        <FFormContext.Provider
          value={{ placeholders: workHistoryData.placeholders } as any}
        >
          <WorkHistoryFormPage
            loading={loading}
            store={store}
            label={label}
            onSubmit={inContainer ? onSubmit : undefined}
            textData={textData}
          />
        </FFormContext.Provider>
      </OverlayRouter>
    );
    return inContainer ? (
      <FormsetWrapper
        hideStep
        header={textData.formTitle.name}
        description={textData.formTitle.subHeader}
        buttonText={textData.buttonText.saveAndContinue}
        buttonProps={{ isLoading: loading, onClick: onBack }}
        shouldDisplayButton={store.workCompleted}
      >
        {render}
      </FormsetWrapper>
    ) : (
      render
    );
  }
);

export default WorkHistoryPage;
